import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';

// Create subscription intent (for Stripe Elements)
export const createSubscriptionIntent = createAsyncThunk(
  'payment/createSubscriptionIntent',
  async ({ planId }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      // FIXED: Send plan_id as query parameter instead of body
      const response = await fetch(`${API_BASE}/payment/create-subscription-intent?plan_id=${planId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        // Remove body since plan_id is now in query params
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create subscription intent');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create checkout session (for Stripe Checkout)
export const createCheckoutSession = createAsyncThunk(
  'payment/createCheckoutSession',
  async ({ planId }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      // FIXED: Send plan_id as query parameter
      const response = await fetch(`${API_BASE}/payment/checkout?plan_id=${planId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Switch subscription plan
export const switchSubscriptionPlan = createAsyncThunk(
  'payment/switchPlan',
  async ({ planId }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }
      
      // Use the correct backend endpoint /payment/upgrade with plan_id as query parameter
      const response = await fetch(`${API_BASE}/payment/upgrade?plan_id=${planId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to switch plan');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch pricing plans
export const fetchPricingPlans = createAsyncThunk(
  'payment/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE}/payment/plans`);

      if (!response.ok) {
        throw new Error('Failed to fetch pricing plans');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch user's subscription
export const fetchMySubscription = createAsyncThunk(
  'payment/fetchMySubscription',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetch(`${API_BASE}/payment/my-subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create Billing Portal Session
export const createPortalSession = createAsyncThunk(
  'payment/createPortalSession',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetch(`${API_BASE}/payment/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create billing portal session');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Cancel Subscription directly
export const cancelSubscription = createAsyncThunk(
  'payment/cancelSubscription',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetch(`${API_BASE}/payment/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel subscription');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  plans: [],
  currentPlan: null,
  subscription: null,
  clientSecret: null,
  subscriptionId: null,
  customerId: null,
  isLoading: false,
  isProcessing: false,
  error: null,
  successMessage: null,
  checkoutUrl: null,
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearPaymentError: (state) => {
      state.error = null;
    },
    clearPaymentSuccess: (state) => {
      state.successMessage = null;
    },
    setPaymentProcessing: (state, action) => {
      state.isProcessing = action.payload;
    },
    setPaymentSuccess: (state, action) => {
      state.successMessage = action.payload;
      state.isProcessing = false;
      state.error = null;
    },
    setPaymentError: (state, action) => {
      state.error = action.payload;
      state.isProcessing = false;
    },
    resetPaymentState: (state) => {
      state.clientSecret = null;
      state.subscriptionId = null;
      state.isProcessing = false;
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Subscription Intent
      .addCase(createSubscriptionIntent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSubscriptionIntent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clientSecret = action.payload.clientSecret;
        state.subscriptionId = action.payload.subscriptionId;
        state.customerId = action.payload.customerId;
      })
      .addCase(createSubscriptionIntent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to initialize payment';
      })
      // Create Checkout Session
      .addCase(createCheckoutSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.checkoutUrl = action.payload.checkout_url;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create checkout session';
      })
      // Fetch Pricing Plans
      .addCase(fetchPricingPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPricingPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plans = action.payload;
      })
      .addCase(fetchPricingPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load pricing plans';
      })
      // Fetch My Subscription
      .addCase(fetchMySubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMySubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscription = action.payload;
      })
      .addCase(fetchMySubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load subscription';
      });
  },
});

export const {
  clearPaymentError,
  clearPaymentSuccess,
  setPaymentProcessing,
  setPaymentSuccess,
  setPaymentError,
  resetPaymentState,
} = paymentSlice.actions;

export default paymentSlice.reducer;